<template>
    <div class="v-dropdown" ref="dropdownRef">
        <div class="v-dropdown-trigger" @click="toggle">
            <slot name="trigger"></slot>
        </div>
        <transition name="fade">
            <div v-if="isOpen" class="v-dropdown-menu" :class="`v-dropdown-menu--${placement}`">
                <slot name="content">
                    <ul class="v-dropdown-list">
                        <li v-for="(item, index) in items" :key="index" class="v-dropdown-item"
                            @click="handleItemClick(item)">
                            <simple-svg v-if="item.icon" :src="item.icon" width="16" height="16"
                                :custom-class-name="item.isRed ? 'icon-red' : 'icon'" />
                            <span v-if="item.label" :class="{ red: item.isRed }">{{ item.label }}</span>
                        </li>
                    </ul>
                </slot>
            </div>
        </transition>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';

export default {
    name: 'VDropdown',
    props: {
        items: {
            type: Array,
            default: () => []
        },
        customContent: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placement: {
            type: String,
            default: 'bottom'
        }
    },
    emits: ['select'],
    setup(props, { emit }) {
        const isOpen = ref(false);
        const dropdownRef = ref(null);

        const toggle = () => {
            if (props.disabled) return;
            isOpen.value = !isOpen.value;
        };

        const close = () => {
            isOpen.value = false;
        };

        const handleItemClick = (item) => {
            emit('select', item);
            close();
        };

        const handleClickOutside = (event) => {
            if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
                close();
            }
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        onUnmounted(() => {
            document.removeEventListener('click', handleClickOutside);
        });

        return {
            isOpen,
            toggle,
            handleItemClick,
            dropdownRef
        };
    }
}
</script>

<style lang="scss" scoped>
.v-dropdown {
    position: relative;
    display: inline-flex;
    align-items: center;

    span {
        white-space: nowrap;
    }
}

.v-dropdown-trigger {
    cursor: pointer;
    display: flex;
    align-items: center;
}

.v-dropdown-menu {
    position: absolute;
    right: 0;
    z-index: 1000;
    min-width: 160px;
    padding: 4px 4px;
    background-color: var(--color-bg);
    border: 1px solid var(--color-border);
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    &--bottom {
        top: 100%;
        margin-top: 4px;
    }

    &--top {
        bottom: 100%;
        margin-bottom: 4px;
    }

    &--top-start {
        bottom: 100%;
        margin-bottom: 4px;
        right: auto;
        left: 0;
    }

    &--bottom-start {
        top: 100%;
        margin-top: 4px;
        right: auto;
        left: 0;
    }
}

.v-dropdown-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.v-dropdown-item {
    padding: 6px 16px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text);
    cursor: pointer;
    transition: background-color 0.2s;

    .red {
        color: var(--color-red);
    }

    &:hover {
        background-color: color-mix(in srgb, var(--color-bg-light) 30%, transparent);
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
